import type { FooterColumn, NavLink } from "@/brand/nav-model"
import { Logo } from "@/core/components/logo"
import { useLocalHref, useUi } from "@/core/i18n/locale-context"
import { getSiteName, getSiteTagline } from "@/core/site"

interface SiteFooterProps {
  columns: FooterColumn[]
  legalLinks?: NavLink[]
  siteName?: string
  tagline?: string
  year?: number
  disclaimer?: string
}

// Link boxes are 44 px tall (32 px from md) for touch; the negative margins on the nav and the legal list cancel exactly the padding
// above and below the text, so the space around the divider and the footer edges is measured from the text. Keep the pairs in step.
const linkBase =
  "hover:text-foreground focus-visible:ring-ring inline-flex items-center rounded-sm outline-none focus-visible:ring-2"
const navLinkClass = `${linkBase} py-3 md:py-1.5`
// One source for the legal row: link padding and the list margin that cancels it (the "gap-y" of wrapped rows is the link box itself).
const LEGAL_LINK_PAD = { base: "py-3.5", md: "md:py-2" }
const LEGAL_LIST_PULL = "-my-3.5 md:-my-2"
const legalLinkClass = `${linkBase} ${LEGAL_LINK_PAD.base} ${LEGAL_LINK_PAD.md}`

export const SiteFooter = ({
  columns,
  legalLinks = [],
  siteName = getSiteName(),
  tagline = getSiteTagline(),
  year = new Date().getFullYear(),
  disclaimer,
}: SiteFooterProps) => {
  const ui = useUi()
  const localHref = useLocalHref()
  return (
    <footer className="border-border bg-card text-muted-foreground px-band border-t py-7 text-sm md:py-10">
      <div className="grid gap-7 lg:grid-cols-[1.4fr_5fr]">
        <div className="text-center lg:text-left">
          <b className="text-foreground flex items-center justify-center gap-2 text-base leading-5 lg:justify-start">
            <Logo className="size-5 shrink-0" />
            {siteName}
          </b>
          {tagline && <p className="mx-auto mt-2 max-w-[34ch] lg:mx-0">{tagline}</p>}
        </div>
        <nav
          aria-label={ui.footerNav}
          className="-mb-3 grid grid-cols-2 gap-7 md:-mb-1.5 md:grid-cols-3 lg:grid-cols-5"
        >
          {columns.map(({ heading, links }) => (
            <div key={heading}>
              <h2 className="text-foreground mb-2.5 text-sm font-semibold">{heading}</h2>
              <ul>
                {links.map(({ label, href }) => (
                  <li key={href}>
                    <a href={localHref(href)} className={navLinkClass}>
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
      <div className="border-border mt-7 grid gap-4 border-t pt-7 text-xs md:mt-10 md:flex md:items-center md:justify-between md:gap-6 md:pt-10">
        {legalLinks.length > 0 && (
          <ul
            aria-label={ui.legalNav}
            className={`${LEGAL_LIST_PULL} flex flex-wrap gap-x-4 md:order-2`}
          >
            {legalLinks.map(({ label, href }) => (
              <li key={href}>
                <a href={localHref(href)} className={legalLinkClass}>
                  {label}
                </a>
              </li>
            ))}
          </ul>
        )}
        <p>
          {disclaimer ?? ui.disclaimer} © {year} {siteName}
        </p>
      </div>
    </footer>
  )
}
